import { AMENITIES, DISRUPTIONS, STATIONS } from "../fixtures";
import addDates from "../utils/addDates";
import capitalize from "../utils/capitalize";
import pick from "../utils/pick";
class CallingPointLookup {
  constructor(defaultValue) {
    this.lookup = {};
    this.defaultValue = defaultValue;
  }
  setDefault(value) {
    this.defaultValue = value;
    return this;
  }
  set(index, value) {
    this.lookup[index] = value;
  }
  get(index) {
    if (index in this.lookup) {
      return this.lookup[index];
    }
    return this.defaultValue;
  }
}
class LegBuilder {
  constructor() {
    this.legIdx = 0;
    this.departAt = new Date("2022-09-09T10:26:39.651Z");
    this.legDurationInMinutes = 30;
    this.legConnectionInMinutes = 0;
    this.departureStationIdx = 0;
    this.differentStationFromPreviousLeg = false;
    this.isTransitLeg = true;
    this.numberOfPreOriginPoints = 0;
    this.numberOfIntermediatePoints = 0;
    this.numberOfPostDestinationPoints = 0;
    this.tsiEnableArrivalData = new CallingPointLookup(true);
    this.tsiEnableDepartureData = new CallingPointLookup(true);
    this.tsiEnableScheduleData = new CallingPointLookup(true);
    this.tsiEnableRealTimeData = new CallingPointLookup(true);
    this.tsiRealTimeStatus = new CallingPointLookup();
    this.tsiRealTimeType = new CallingPointLookup();
    this.transportType = "train";
    this.legAmenities = [];
    this.legDisruptions = [];
    this.tsiLegDisruptions = [];
    this.isFirstDisruption = false;
  }
  get computedDepartAt() {
    return addDates(this.departAt, { minutes: this.legConnectionInMinutes }).toISOString();
  }
  get computedArrivalAt() {
    return addDates(this.computedDepartAt, { minutes: this.legDurationInMinutes }).toISOString();
  }
  get arrivalStationIdx() {
    return this.departureStationIdx + this.numberOfIntermediatePoints + 1;
  }
  get computedOrigin() {
    return LegBuilder.mockLegLocation(this.departureStationIdx);
  }
  get computedDestination() {
    return LegBuilder.mockLegLocation(this.arrivalStationIdx);
  }
  get transportMode() {
    const { legIdx, transportType } = this;
    return {
      id: `transport-mode-${legIdx}`,
      code: `transport-mode-code-${legIdx}`,
      name: capitalize(transportType),
      mode: transportType
    };
  }
  get totalNumberOfPoints() {
    return this.numberOfPreOriginPoints + this.numberOfIntermediatePoints + this.numberOfPostDestinationPoints + 2;
  }
  get pointDurationTime() {
    const { legDurationInMinutes: minutes } = this;
    return addDates({ minutes }).getTime() / (this.numberOfIntermediatePoints + 1);
  }
  get pointDurationOffsetTime() {
    return addDates(this.pointDurationTime * -this.numberOfPreOriginPoints);
  }
  setDepartAt(date) {
    this.departAt = new Date(date);
    return this;
  }
  setDurationTime(minutes) {
    this.legDurationInMinutes = minutes;
    return this;
  }
  setConnectionTime(minutes, fromDifferentStation = false) {
    this.legConnectionInMinutes = minutes;
    this.differentStationFromPreviousLeg = fromDifferentStation;
    return this;
  }
  setDepartureStation(index) {
    if (index > STATIONS.length) {
      console.warn("Departure station index outside of options range.");
    }
    this.departureStationIdx = index;
    return this;
  }
  setTransportType(type) {
    this.transportType = type;
    return this;
  }
  isTransferLeg(transportType = "rapidTransit") {
    this.isTransitLeg = false;
    return this.setTransportType(transportType);
  }
  setFirstDisruption() {
    this.isFirstDisruption = true;
    return this;
  }
  addLegAmenity(legAmenity) {
    const index = this.legAmenities.length;
    if (index > AMENITIES.length) {
      console.warn("Amenity index outside of options range. Duplicated amenities will be shown.");
    }
    this.legAmenities.push({ ...pick(index, AMENITIES), ...legAmenity });
    return this;
  }
  addLegAmenities(n) {
    for (let i = 0; i < n; i += 1) {
      this.addLegAmenity();
    }
    return this;
  }
  addLegDisruption(legDisruption) {
    const index = this.legDisruptions.length;
    this.legDisruptions.push({
      id: `leg-disruption-${index}`,
      lastUpdated: new Date().toISOString(),
      severity: 1,
      disruptionUrl: `https://www.external.com/${index}`,
      trainlineUrl: `https://www.external.com/${index}`,
      message: pick(index, DISRUPTIONS),
      ...legDisruption
    });
    return this;
  }
  addLegDisruptions(n) {
    for (let i = 0; i < n; i += 1) {
      this.addLegDisruption();
    }
    return this;
  }
  addTSILegDisruption(tsiLegDisruption) {
    const index = this.tsiLegDisruptions.length;
    this.tsiLegDisruptions.push({
      id: `tsi-leg-disruption-${index}`,
      lastUpdated: new Date().toISOString(),
      severity: 1,
      trainlineUrl: `https://www.external.com/${index}`,
      message: pick(index, DISRUPTIONS),
      category: "Disruption",
      source: {
        id: `tsi-leg-disruption-source-${index}`,
        name: "Nre",
        url: `http://nationalrail.co.uk/service_disruptions/${index}.aspx`
      },
      status: "Active",
      ...tsiLegDisruption
    });
    return this;
  }
  addTSILegDisruptions(n) {
    for (let i = 0; i < n; i += 1) {
      this.addTSILegDisruption();
    }
    return this;
  }
  setNumberOfPreOriginPoints(amount) {
    this.numberOfPreOriginPoints = amount;
    return this;
  }
  setNumberOfIntermediatePoints(amount) {
    this.numberOfIntermediatePoints = amount;
    return this;
  }
  setNumberOfPostDestinationPoints(amount) {
    this.numberOfPostDestinationPoints = amount;
    return this;
  }
  setTSIArrivalDataEnabled(enabled) {
    this.tsiEnableArrivalData.setDefault(enabled);
    return this;
  }
  setTSIPointArrivalDataEnabled(pointIdx, enabled) {
    this.tsiEnableArrivalData.set(pointIdx, enabled);
    return this;
  }
  setTSIDepartureDataEnabled(enabled) {
    this.tsiEnableDepartureData.setDefault(enabled);
    return this;
  }
  setTSIPointDepartureDataEnabled(pointIdx, enabled) {
    this.tsiEnableDepartureData.set(pointIdx, enabled);
    return this;
  }
  setTSIScheduleDataEnabled(enabled) {
    this.tsiEnableScheduleData.setDefault(enabled);
    return this;
  }
  setTSIPointScheduleDataEnabled(pointIdx, enabled) {
    this.tsiEnableScheduleData.set(pointIdx, enabled);
    return this;
  }
  setTSIRealTimeDataEnabled(enabled) {
    this.tsiEnableScheduleData.setDefault(enabled);
    return this;
  }
  setTSIPointRealTimeDataEnabled(pointIdx, enabled) {
    this.tsiEnableRealTimeData.set(pointIdx, enabled);
    return this;
  }
  setTSIRealTimeStatus(status) {
    this.tsiRealTimeStatus.setDefault(status);
    return this;
  }
  setTSIPointRealTimeStatus(pointIdx, status) {
    this.tsiRealTimeStatus.set(pointIdx, status);
    return this;
  }
  setTSIRealTimeType(type) {
    this.tsiRealTimeType.setDefault(type);
    return this;
  }
  setTSIPointRealTimeType(pointIdx, type) {
    this.tsiRealTimeType.set(pointIdx, type);
    return this;
  }
  getPointTime(pointIdx) {
    return addDates(this.computedDepartAt, this.pointDurationTime * pointIdx, this.pointDurationOffsetTime).toISOString();
  }
  getPointCallingType(pointIdx) {
    if (pointIdx === 0)
      return "ORIGIN";
    if (pointIdx === this.totalNumberOfPoints - 1)
      return "DESTINATION";
    return "NORMAL";
  }
  getPointStationIdx(pointIdx) {
    if (pointIdx === this.numberOfPreOriginPoints) {
      return this.departureStationIdx;
    }
    if (pointIdx === this.numberOfPreOriginPoints + this.numberOfIntermediatePoints + 1) {
      return this.arrivalStationIdx;
    }
    return pointIdx;
  }
  buildLeg() {
    const {
      legIdx,
      transportType,
      legAmenities: amenities,
      legDisruptions: disruptions,
      isFirstDisruption,
      isTransitLeg,
      computedDepartAt: localDepartAt,
      computedArrivalAt: localArriveAt,
      computedOrigin: origin,
      computedDestination: destination
    } = this;
    const leg = {
      id: `leg-${legIdx}`,
      localDepartAt,
      localArriveAt,
      origin,
      destination,
      carrierName: "Avanti West Coast",
      carrier: {
        id: "AWC",
        name: "Avanti West Coast",
        route: "122",
        mode: "train"
      },
      timetableId: `VT776${legIdx}`,
      transportModeName: transportType,
      amenities,
      disruptions,
      firstDisruption: isFirstDisruption
    };
    if (!isTransitLeg) {
      leg.transportMode = this.transportMode;
    }
    return leg;
  }
  buildTSIJunctureData(pointIdx) {
    const time = this.getPointTime(pointIdx);
    const platform = pointIdx.toString();
    const juncture = {};
    if (this.tsiEnableScheduleData.get(pointIdx)) {
      juncture.schedule = { time, platform };
    }
    if (this.tsiEnableRealTimeData.get(pointIdx)) {
      const status = this.tsiRealTimeStatus.get(pointIdx) || null;
      const type = this.tsiRealTimeType.get(pointIdx) || null;
      juncture.realTime = { time, platform, status, type };
    }
    return juncture;
  }
  buildTSICallingPoint(pointIdx) {
    const stationIdx = this.getPointStationIdx(pointIdx);
    const callingPoint = {
      index: pointIdx,
      callingType: this.getPointCallingType(pointIdx),
      location: {
        genericCode: void 0,
        inventoryCode: `urn:trainline:atoc:loc:EUS144${pointIdx}`,
        name: pick(stationIdx, STATIONS),
        shortName: pick(stationIdx, STATIONS).slice(0, 4).toUpperCase()
      }
    };
    if (this.tsiEnableArrivalData.get(pointIdx)) {
      callingPoint.arrival = this.buildTSIJunctureData(pointIdx);
    }
    if (this.tsiEnableDepartureData.get(pointIdx)) {
      callingPoint.departure = this.buildTSIJunctureData(pointIdx);
    }
    return callingPoint;
  }
  buildTSILeg() {
    return {
      timetableId: `VT776${this.legIdx}`,
      carrier: { code: "", name: "" },
      customerDestination: "",
      customerOrigin: "",
      departureDate: "",
      disruptions: this.tsiLegDisruptions,
      transportMode: { code: "", name: "" },
      callingPoints: Array(this.totalNumberOfPoints).fill(0).map((_, pointIdx) => this.buildTSICallingPoint(pointIdx))
    };
  }
  syncWithPreviousLeg(prevLeg) {
    this.legIdx = prevLeg.legIdx + 1;
    this.departAt = new Date(prevLeg.computedArrivalAt);
    if (this.differentStationFromPreviousLeg) {
      this.departureStationIdx = prevLeg.arrivalStationIdx + 1;
    } else {
      this.departureStationIdx = prevLeg.arrivalStationIdx;
    }
    return this;
  }
  static pick(idx, arr) {
    if (idx >= 0)
      return arr[idx % arr.length];
    return arr[Math.abs(idx + arr.length) % arr.length];
  }
  static mockLegLocation(idx) {
    const name = pick(idx, STATIONS);
    const geo = 1 * idx;
    return { id: `location-${idx}`, name, code: `urn:${name}`, latitude: geo, longitude: geo };
  }
}
export {
  LegBuilder as default
};
